import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  useTheme,
  Container,
  Button,
  Stack,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoricalReportsTable from "./HistoricalReportsTable";
import ReportDataDialog from "./ReportDataDialog";
import { useReportDataLoader } from "./useReportDataLoader";
import { FundCountCols, TopwaterTradeCols } from "../../component/Methods";
import { clearReportPopup } from "../../Redux/Reducers/reportsSlice";
import { selectReports ,selectReportPopupInfo} from "../../Redux/Reducers/selectors";

const HistoricalReports = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const reports = useSelector(selectReports);
  const reportPopupInfo = useSelector(selectReportPopupInfo);
  const [pageTitle, setPageTitle] = useState("");
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [reportType, setReportType] = useState("");
  const [columns, setColumns] = useState(FundCountCols);
  const dispatch = useDispatch();
  const { loadReportData, handleDataEdit } = useReportDataLoader();

  useEffect(() => {
    if (reportPopupInfo && reportPopupInfo.name) {
      setRecordForEdit(reportPopupInfo.generation);
      setTitle(reportPopupInfo.name);
      if (
        reportPopupInfo.name.toLowerCase().includes("fundcount") &&
        reportPopupInfo.generation.stage == 0
      ) {
        setReportType("fundcount");
        setColumns(FundCountCols);
        setDialogOpen(true);
      } else if (
        reportPopupInfo.name.toLowerCase().includes("charles") &&
        reportPopupInfo.generation.stage == 0
      ) {
        setReportType("charles");
        setColumns(FundCountCols);
        setDialogOpen(true);
      } else if (
        reportPopupInfo.name.toLowerCase().includes("goldman") &&
        reportPopupInfo.generation.stage == 0
      ) {
        setReportType("goldman");
        setColumns(FundCountCols);
        setDialogOpen(true);
      } else if (
        reportPopupInfo.name.toLowerCase().includes("topwater") &&
        reportPopupInfo.generation.stage == 0
      ) {
        setReportType("topwater");
        setColumns(TopwaterTradeCols);
        setDialogOpen(true);
      } else {
        setReportType("report");
        setColumns(FundCountCols);
      }

      dispatch(clearReportPopup());
    }
  }, [reportPopupInfo, dispatch]);

  useEffect(() => {
    if (reports && id) {
      const findReport = reports.find((i) => {
        const reportId = i.reportId;
        const searchId = id;

        return String(reportId) === String(searchId);
      });

      const reportName = findReport?.name;
      const title = `${reportName ? reportName + "'s " : ""}Historical Reports`;
      setPageTitle(title);
      setTitle(reportName || "");
      if (reportName) {
        const name = reportName.toLowerCase();
        if (name.includes("fundcount")) {
          setReportType("fundcount");
          setColumns(FundCountCols);
        } else if (name.includes("charles")) {
          setReportType("charles");
          setColumns(FundCountCols); // Charles uses same columns as FundCount
        } else if (name.includes("goldman")) {
          setReportType("goldman");
          setColumns(FundCountCols); // Goldman uses same columns as FundCount
        } else if (name.includes("topwater")) {
          setReportType("topwater");
          setColumns(TopwaterTradeCols);
        } else {
          setReportType("report");
          setColumns(FundCountCols); // Default to FundCount columns
        }
      }
    } else {
      setPageTitle("Historical Reports");
    }
  }, [reports, id]);

  const handleBack = () => {
    navigate("/reports");
  };

  const handleViewReportData = (row) => {
    setRecordForEdit(row);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setRecordForEdit(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[50],
        minHeight: "100vh",
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {pageTitle}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              variant="contained"
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Back to Reports
            </Button>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            View historical reports and generation details
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <HistoricalReportsTable
            reportId={id}
            onViewReportData={handleViewReportData}
          />
        </Card>
      </Container>

      {/* Modern Report Data Dialog */}
      <ReportDataDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={title}
        recordForEdit={recordForEdit}
        reportType={reportType}
        columns={columns}
        onDataLoad={loadReportData}
        onDataEdit={handleDataEdit}
      />
    </Box>
  );
};

export default HistoricalReports;
